import { useCallback, useEffect, useRef, useState } from "react";

import {
  CacheStatus,
  GetCachedMessagesPagination as Pagination,
  GetCachedMessagesQueryParams as QueryParams,
  GetCachedMessagesResult,
  MessageItem,
} from "@/components/cached-messages/types";
import { useNotifications } from "@/contexts/NotificationsContext";
import { ADMIN_DEFAULT_PAGE_SIZE } from "@/utils/adminTable";

import { useApi } from "../useApi";

interface UseAdminCachedMessagesProps {
  /** Rows per page to request. Defaults to the admin tables' page size. */
  pageSize?: number;
}

export const useAdminCachedMessages = ({
  pageSize = ADMIN_DEFAULT_PAGE_SIZE,
}: UseAdminCachedMessagesProps) => {
  const [items, setItems] = useState<MessageItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  /**
   * The default order is part of the first request, not something applied to the
   * rows it returns: this list pages server-side, so the client only ever holds
   * one page and cannot order the rest of the table.
   *
   * `lastUpdated` descending puts the most recently touched entries on page 1,
   * which is what this table is used to find. It is a stored field of the item,
   * so the endpoint orders by it directly without resolving anything.
   *
   * The literal is pinned on both sides of the wire: the handler lists
   * `lastUpdated` in its `valid_sort_fields`, and an endpoint that does not know
   * a key falls back to `createdTimestamp` and still answers 200. So a rename on
   * either side degrades the order rather than failing the page — which is why
   * tests on both sides assert the string rather than trusting it.
   */
  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    pageSize,
    sortBy: "lastUpdated",
    sortOrder: "desc",
  });
  const [loading, setLoading] = useState(false);
  // Guard against stale responses overwriting newer results
  const fetchIdRef = useRef(0);

  const { notifyError } = useNotifications();
  const { apiGet } = useApi({ isAdmin: true });

  const fetchCachedMessages = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: queryParams.page.toString(),
        pageSize: queryParams.pageSize.toString(),
      });
      if (queryParams.sortBy) params.set("sortBy", queryParams.sortBy);
      if (queryParams.sortOrder) params.set("sortOrder", queryParams.sortOrder);
      if (queryParams.userId) params.set("userId", queryParams.userId);
      if (queryParams.cacheStatus) params.set("cacheStatus", queryParams.cacheStatus);
      if (queryParams.search) params.set("search", queryParams.search);

      const response = await apiGet<GetCachedMessagesResult>(
        `/admin/cached-messages?${params.toString()}`
      );

      // Ignore response if a newer fetch was initiated while this was in flight
      if (currentFetchId !== fetchIdRef.current) return;

      if (response?.messages) {
        setItems(response.messages);
        setPagination(response.pagination);
      } else {
        notifyError("Cached Messages Error.", "Failed to load cached messages");
      }
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error(error);
      notifyError("Cached Messages Error.", "Failed to load cached messages");
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiGet, queryParams, notifyError]);

  /**
   * Move the named rows to a cache state, without refetching the list.
   *
   * The list endpoint pages a full DynamoDB `Scan` and then filters and sorts in
   * Python, so refetching it after a single-row action costs seconds and drops
   * the whole table into its loading state. Caching one entry changes one row, so
   * this edits those rows in place instead.
   *
   * Rows the action did not settle are returned as the *same objects*, not
   * rebuilt copies, so the table re-renders only the cells that changed. Ids that
   * are not on the current page are ignored -- a bulk action's response can name
   * an entry that a concurrent sort or filter has since paged away.
   *
   * `lastUpdated` is deliberately left as the server last reported it. The server
   * does stamp it on a status change, but it does not return the new value here,
   * and writing a browser clock's time into a column that otherwise always shows
   * server time would put a fabricated value on screen. The explicit Refresh
   * button, and any sort/filter/page change, pull the authoritative row.
   *
   * Identity is stable for the lifetime of the hook. `CachedMessages` closes over
   * this inside the `useMemo` that builds `columnDefinitions`, so a callback
   * recreated per render would rebuild the columns and re-render every row --
   * reintroducing the cost this exists to remove.
   */
  const applyCachedState = useCallback((ids: string[], cached: boolean) => {
    const affected = new Set(ids);
    if (affected.size === 0) return;

    setItems((prevItems) => {
      let changed = false;
      const nextItems = prevItems.map((item) => {
        if (!affected.has(item.id) || item.cached === cached) return item;
        changed = true;
        return {
          ...item,
          cached,
          cacheStatus: cached ? CacheStatus.ACTIVE : CacheStatus.INACTIVE,
        };
      });
      return changed ? nextItems : prevItems;
    });
  }, []);

  const updateQueryParams = useCallback(
    (newQueryParams: Partial<QueryParams>) => {
      setQueryParams((prevParams) => ({
        ...prevParams,
        ...newQueryParams,
      }));
    },
    []
  );

  // Run on load.
  useEffect(() => {
    fetchCachedMessages();
  }, [fetchCachedMessages]);

  useEffect(() => {
    // Reset to page 1 if page size changes.
    updateQueryParams({ page: 1, pageSize });
  }, [pageSize, updateQueryParams]);

  return {
    items,
    pagination,
    queryParams,
    loading: loading,
    fetchCachedMessages,
    updateQueryParams,
    applyCachedState,
  };
};
